import type {
  ApprovalStatus,
  Business,
  BusinessFilters,
  ConnectedService,
  Customer,
} from "@/types/database";
import type { ChartDatum } from "@/lib/analytics";
import { BRAND } from "@/lib/brand-colors";

export type BusinessOverview = {
  totalBusinesses: number;
  pendingApprovals: number;
  activeServices: number;
  totalCustomers: number;
  shftOriginalCount: number;
};

export type BusinessListEntry = Business & {
  connectedService: ConnectedService | null;
  customerCount: number;
  priceCount: number;
  subscriberCount: number;
};

export function deriveConnectedServiceStatus(
  service: ConnectedService | null | undefined
): ApprovalStatus | "none" {
  if (!service) return "none";
  if (service.approvalStatus === "approved" || service.approvalStatus === "rejected") {
    return service.approvalStatus;
  }
  if (service.approvalStatus === "pending") return "pending";
  return service.isActive ? "approved" : "pending";
}

export function deriveBusinessStatus(business: Business): ApprovalStatus {
  if (business.approvalStatus === "approved" || business.approvalStatus === "rejected") {
    return business.approvalStatus;
  }
  return "pending";
}

export function getBusinessOverview(
  businesses: Business[],
  services: ConnectedService[],
  customers: Customer[]
): BusinessOverview {
  return {
    totalBusinesses: businesses.length,
    pendingApprovals: services.filter((service) => deriveConnectedServiceStatus(service) === "pending")
      .length,
    activeServices: services.filter((service) => service.isActive === true).length,
    totalCustomers: customers.length,
    shftOriginalCount: businesses.filter((business) => business.isShftOriginal === true).length,
  };
}

export function getBusinessesByType(businesses: Business[]): ChartDatum[] {
  const counts = new Map<string, number>();

  for (const business of businesses) {
    const type = business.type?.trim() || "Unspecified";
    counts.set(type, (counts.get(type) ?? 0) + 1);
  }

  return [...counts.entries()]
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
}

export function getConnectedServiceStatusChart(
  services: ConnectedService[]
): Array<ChartDatum & { fill: string }> {
  const counts: Record<ApprovalStatus, number> = {
    pending: 0,
    approved: 0,
    rejected: 0,
  };

  for (const service of services) {
    const status = deriveConnectedServiceStatus(service);
    if (status === "none") continue;
    counts[status] += 1;
  }

  return [
    { name: "Pending", value: counts.pending, fill: "#FCD34D" },
    { name: "Approved", value: counts.approved, fill: BRAND.accent },
    { name: "Rejected", value: counts.rejected, fill: "#F97316" },
  ].filter((entry) => entry.value > 0);
}

export function buildBusinessListEntries(
  businesses: Business[],
  services: ConnectedService[],
  customers: Customer[],
  pricesByBusiness: Map<string, number> = new Map()
): BusinessListEntry[] {
  const serviceByBusinessId = new Map<string, ConnectedService>();
  for (const service of services) {
    if (!service.businessID) continue;
    if (!serviceByBusinessId.has(service.businessID)) {
      serviceByBusinessId.set(service.businessID, service);
    }
  }

  const customerCounts = new Map<string, number>();
  for (const customer of customers) {
    if (!customer.businessID) continue;
    customerCounts.set(customer.businessID, (customerCounts.get(customer.businessID) ?? 0) + 1);
  }

  return businesses.map((business) => {
    const businessID = business.businessID ?? "";
    const connectedService = businessID ? (serviceByBusinessId.get(businessID) ?? null) : null;
    return {
      ...business,
      connectedService,
      customerCount: businessID ? (customerCounts.get(businessID) ?? 0) : 0,
      priceCount: businessID ? (pricesByBusiness.get(businessID) ?? 0) : 0,
      subscriberCount: connectedService?.carID?.length ?? 0,
    };
  });
}

export function defaultBusinessFilters(): BusinessFilters {
  return {
    search: "",
    type: "",
    country: "",
    approvalStatus: "",
    hasConnectedService: "",
  };
}

export function filterBusinesses(
  entries: BusinessListEntry[],
  filters: BusinessFilters
): BusinessListEntry[] {
  const query = filters.search.trim().toLowerCase();

  return entries.filter((entry) => {
    if (filters.type && entry.type !== filters.type) return false;
    if (filters.country && entry.country !== filters.country) return false;

    const serviceStatus = deriveConnectedServiceStatus(entry.connectedService);
    if (filters.approvalStatus) {
      if (filters.approvalStatus === "none") {
        if (serviceStatus !== "none") return false;
      } else if (serviceStatus !== filters.approvalStatus) {
        return false;
      }
    }

    if (filters.hasConnectedService === "yes" && !entry.connectedService) return false;
    if (filters.hasConnectedService === "no" && entry.connectedService) return false;

    if (!query) return true;

    const haystack = [
      entry.name,
      entry.email,
      entry.country,
      entry.type,
      entry.slug,
      entry.businessID,
      entry.address,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return haystack.includes(query);
  });
}

export function filterConnectedServices(
  services: ConnectedService[],
  opts: { search?: string; status?: string } = {}
): ConnectedService[] {
  const query = opts.search?.trim().toLowerCase() ?? "";
  const status = opts.status ?? "";

  return services.filter((service) => {
    const derived = deriveConnectedServiceStatus(service);
    if (status && derived !== status) return false;
    if (!query) return true;

    const haystack = [
      service.businessName,
      service.businessType,
      service.businessID,
      service.promotionalText,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return haystack.includes(query);
  });
}
